import { useState } from 'react';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { useMfa, type EnrollResult } from '../../hooks/useMfa';

/**
 * Twee-factor-authenticatie (TOTP) voor het eigen account.
 * Werkt met 1Password, Google Authenticator én de YubiKey (Yubico Authenticator).
 * Alleen enrollment/beheer — de login-uitdaging komt in een aparte stap.
 */
export default function MfaSection() {
  const { verified, loading, factorId, enroll, verifyEnroll, unenroll } = useMfa();
  const [enrolling, setEnrolling] = useState<EnrollResult | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const startEnroll = async () => {
    setError('');
    setBusy(true);
    try {
      setEnrolling(await enroll());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kon MFA niet starten');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (!enrolling) return;
    setError('');
    setBusy(true);
    try {
      await verifyEnroll(enrolling.factorId, code);
      setEnrolling(null);
      setCode('');
    } catch {
      setError('Onjuiste code. Controleer de code in je app en probeer opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  const removeMfa = async () => {
    if (!factorId) return;
    setBusy(true);
    setError('');
    try {
      await unenroll(factorId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kon MFA niet uitschakelen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-theme-border pt-4 mt-2">
      <div className="flex items-center gap-2 mb-2">
        {verified ? (
          <ShieldCheck className="w-4 h-4 text-success" />
        ) : (
          <ShieldOff className="w-4 h-4 text-foreground-tertiary" />
        )}
        <h3 className="text-sm font-medium text-foreground">Twee-factor-authenticatie</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laden…
        </div>
      ) : verified ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-foreground">
            <ShieldCheck className="w-4 h-4 text-success shrink-0" />
            <span>MFA staat aan. Bij het inloggen wordt naast je wachtwoord een code gevraagd.</span>
          </div>
          <button
            type="button"
            onClick={removeMfa}
            disabled={busy}
            className="text-xs font-medium text-error hover:underline disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'MFA uitschakelen'}
          </button>
        </div>
      ) : enrolling ? (
        <div className="space-y-3">
          <p className="text-xs text-foreground-secondary">
            Scan de QR-code met je authenticator-app (1Password, Google Authenticator, of de
            Yubico Authenticator met je YubiKey) en voer daarna de 6-cijferige code in.
          </p>
          <div className="flex justify-center">
            <img
              src={enrolling.qrCode}
              alt="MFA QR-code"
              className="w-40 h-40 rounded-lg bg-white p-2"
            />
          </div>
          <details className="text-xs text-foreground-tertiary">
            <summary className="cursor-pointer">Handmatig invoeren (setup-sleutel)</summary>
            <code className="mt-1 block break-all rounded bg-surface-secondary p-2 text-foreground-secondary">
              {enrolling.secret}
            </code>
          </details>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6-cijferige code"
            className="w-full px-3 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-[#F598AF] focus:border-transparent outline-none transition-all text-sm tracking-widest text-center"
          />
          {error && <p className="text-xs text-error">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirmEnroll}
              disabled={busy || code.length !== 6}
              className="flex-1 px-4 py-2 text-xs font-medium text-white bg-[#F598AF] rounded-lg hover:bg-[#E77B9B] transition-colors disabled:bg-surface-tertiary disabled:cursor-not-allowed"
            >
              {busy ? 'Controleren…' : 'Bevestigen'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEnrolling(null);
                setCode('');
                setError('');
              }}
              disabled={busy}
              className="px-4 py-2 text-xs font-medium text-foreground-secondary border border-theme-border rounded-lg hover:bg-surface-tertiary transition-colors"
            >
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-foreground-secondary">
            Beveilig je account met een extra code bij het inloggen. Aanbevolen voor iedereen.
          </p>
          {error && <p className="text-xs text-error">{error}</p>}
          <button
            type="button"
            onClick={startEnroll}
            disabled={busy}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-[#F598AF] rounded-lg hover:bg-[#E77B9B] transition-colors disabled:opacity-50"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {busy ? 'Bezig…' : 'MFA aanzetten'}
          </button>
        </div>
      )}
    </div>
  );
}
